import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, Popconfirm } from "antd";
import { FC, useCallback, useState } from "react";
import { Payment } from "tm-shared/types";
import { formatUAHNarrow } from "tm-shared/utils";

import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { StyledPaymentItem } from "./Styled";

// --- Props ---

interface PaymentItemProps {
    payment: Payment;
    employeeId: string;
    onDeletePayments: (paymentIds: string[]) => Promise<void>;
    deleteLoading: boolean;
    canDelete: boolean;
    isManager: boolean;
}

// --- Payment item ---

export const PaymentItem: FC<PaymentItemProps> = ({
    payment,
    employeeId,
    deleteLoading,
    onDeletePayments,
    canDelete,
    isManager,
}) => {
    // --- Local state ---

    const [localDeleteLoading, setLocalDeleteLoading] = useState(false);

    // --- Derived state ---

    const { paymentIds, consignmentId, payerFullName, payer, paidAmount } =
        payment;

    const canDeleteLocal = !payment.isExported && canDelete;

    const isCurrentPayer = payer === employeeId;
    const canDeleteCurrentPayment = isCurrentPayer || isManager;

    // --- UI actions ---

    const handleDelete = useCallback(async () => {
        if (!paymentIds) return;

        setLocalDeleteLoading(true);

        await onDeletePayments(paymentIds);

        setLocalDeleteLoading(false);
    }, [onDeletePayments, paymentIds]);

    // --- Render ---

    return (
        <StyledPaymentItem>
            <PrimaryText>{consignmentId}</PrimaryText>

            {!isCurrentPayer && (
                <Flex gap={16}>
                    <SecondaryText fontSize={16}>Платник:</SecondaryText>

                    <PrimaryText fontSize={16} textColor="accent">
                        {payerFullName}
                    </PrimaryText>
                </Flex>
            )}

            <Flex gap={8} justify="space-between" align="center">
                <Flex gap={8}>
                    <SecondaryText fontSize={16}>Оплата:</SecondaryText>

                    <PrimaryText textColor="accent" fontSize={18}>
                        {formatUAHNarrow(paidAmount ?? 0)}
                    </PrimaryText>
                </Flex>

                {canDeleteLocal && canDeleteCurrentPayment && (
                    <Popconfirm
                        title={"Видалити оплату?"}
                        onConfirm={handleDelete}
                        okText="Так"
                        cancelText="Ні"
                        disabled={deleteLoading}
                    >
                        <Button
                            type="text"
                            loading={localDeleteLoading}
                            disabled={deleteLoading}
                            icon={
                                <PrimaryText
                                    textColor={
                                        deleteLoading ? "disabled" : "error"
                                    }
                                    fontSize={18}
                                >
                                    <DeleteOutlined />
                                </PrimaryText>
                            }
                        />
                    </Popconfirm>
                )}
            </Flex>
        </StyledPaymentItem>
    );
};
